import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../utils/auth';
import { authHeaders } from '../utils/api';
import { useCurrency } from '../utils/currency';
import {
  DashboardIcon, PortfolioIcon, ImportIcon, AIIcon,
  InsightsIcon, ScreenerIcon, TickerIcon, AlertsIcon, SaveIcon
} from './Icons';
import '../styles/sidebar.css';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/', icon: DashboardIcon },
  { label: 'Portfolio Manager', path: '/portfolio', icon: PortfolioIcon },
  { label: 'Import Holdings', path: '/import', icon: ImportIcon },
  { label: 'AI Analyst Chat', path: '/chat', icon: AIIcon },
  { label: 'Daily Insights', path: '/insights', icon: InsightsIcon },
  { label: 'Stock Screener', path: '/screener', icon: ScreenerIcon },
  { label: 'Live Ticker', path: '/ticker', icon: TickerIcon },
  { label: 'Alerts & Simulator', path: '/alerts', icon: AlertsIcon },
];

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const currency = useCurrency();
  const [time, setTime] = useState(new Date());
  const [saveMsg, setSaveMsg] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSave = async () => {
    try {
      const res = await fetch('/api/portfolio/save', { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }), body: '{}' });
      if (res.ok) {
        setSaveMsg('Portfolio saved!');
        setTimeout(() => setSaveMsg(''), 2000);
      }
    } catch {
      setSaveMsg('Save failed');
      setTimeout(() => setSaveMsg(''), 2000);
    }
  };

  const timeStr = time.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' + time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  return (
    <>
      <button className="mobile-menu-toggle" onClick={() => setMobileOpen(true)} aria-label="Open menu">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#00ff9d" strokeWidth="2" strokeLinecap="round">
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <aside className={`ie-sidebar${mobileOpen ? ' open' : ''}`}>
        <div className="mobile-close-row">
          <button className="mobile-close-btn" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="ie-brand">
          <img src="/logo3.png" alt="Retail Alpha" style={{ width: '100%', objectFit: 'contain', display: 'block', borderRadius: 4 }} />
          <p style={{ color: '#64748b', fontSize: 10, textAlign: 'center', margin: '4px 0 0', letterSpacing: 0.3 }}>AI-Powered Alpha for Retail Investors</p>
        </div>
        <div className="ie-nav-divider" />
        {currency && (
          <div style={{ padding: '6px 14px 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#64748b', fontSize: 10, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>Display</span>
            <div
              onClick={currency.toggleCurrency}
              style={{
                flex: 1,
                display: 'flex',
                background: 'rgba(255,255,255,0.04)',
                borderRadius: 8,
                padding: 2,
                border: '1px solid rgba(255,255,255,0.08)',
                cursor: 'pointer',
                position: 'relative',
              }}
            >
              <div style={{
                flex: 1,
                textAlign: 'center',
                padding: '5px 0',
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 6,
                transition: 'all 0.2s',
                color: currency.displayCurrency === 'USD' ? '#00ff9d' : '#64748b',
                background: currency.displayCurrency === 'USD' ? 'rgba(0,255,157,0.12)' : 'transparent',
              }}>USD</div>
              <div style={{
                flex: 1,
                textAlign: 'center',
                padding: '5px 0',
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 6,
                transition: 'all 0.2s',
                color: currency.displayCurrency === 'CAD' ? '#00ff9d' : '#64748b',
                background: currency.displayCurrency === 'CAD' ? 'rgba(0,255,157,0.12)' : 'transparent',
              }}>CAD</div>
            </div>
          </div>
        )}
        <nav className="ie-nav-list">
          {NAV_ITEMS.map(({ label, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) => `ie-nav-btn${isActive ? ' active' : ''}`}
            >
              <span className="ie-nav-ico">
                <Icon size={17} />
              </span>
              <span className="ie-nav-label">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="ie-nav-divider" style={{ marginTop: 12, marginBottom: 12 }} />
        <button className="ie-save-btn" onClick={handleSave}>
          <span className="ie-nav-ico">
            <SaveIcon size={17} />
          </span>
          {saveMsg || 'Save Portfolio'}
        </button>
        <div className="ie-live">
          <div className="ie-live-dot" />
          <span className="ie-live-text">LIVE • {timeStr}</span>
        </div>
        {user && (
          <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 8 }}>
            <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.display_name || user.email}</div>
            <button onClick={handleLogout} style={{ width: '100%', padding: '8px', background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.2)', borderRadius: 8, color: '#ff4d4d', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'Inter, sans-serif' }}>Sign Out</button>
          </div>
        )}
      </aside>
    </>
  );
}

export default Sidebar;
